import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import {
  useForkConversation,
  useDeleteConversation,
} from '@/hooks/use-conversations-v2';
import { ConfirmModal } from '@/components/ui';
import { SUPPORT_EMAIL, supportMailto } from '@/lib/support';
import type { MissingModelDetails } from '@/lib/api';

interface MissingModelPanelProps {
  conversationId: string;
  conversationTitle: string;
  missing: MissingModelDetails;
  /**
   * Called after a successful fork. The Builder route owns
   * `activeConversationId` and the fork-success banner state — pass `newId`
   * to switch the active conversation and `orphanedInputCount` to populate
   * the banner.
   */
  onForked: (newConversationId: string, orphanedInputCount: number) => void;
  /**
   * Called after a successful delete. The Builder route clears
   * `activeConversationId`; the existing auto-select effect picks the next
   * conversation in the list (or the empty state from Session 11).
   */
  onDeleted: () => void;
}

/**
 * Renders when a conversation references a `(modelId, modelVersion)` that's
 * no longer in the registry. Two branches:
 *
 *   - `latestAvailable` is non-null → primary action "Fork to <id>@<latest>".
 *     Forks the conversation onto the latest version, carrying every input
 *     that still resolves and surfacing the orphaned-input count to the user
 *     via the Builder's fork-success banner.
 *
 *   - `latestAvailable` is null → no other version of the model exists.
 *     Primary action becomes destructive: "Delete this conversation".
 *
 * "Contact admin" mailto is present in both branches as a soft fallback.
 */
export function MissingModelPanel({
  conversationId,
  conversationTitle,
  missing,
  onForked,
  onDeleted,
}: MissingModelPanelProps) {
  const fork = useForkConversation();
  const del = useDeleteConversation();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleFork = async () => {
    if (!missing.latestAvailable) return;
    const result = await fork.mutateAsync({
      conversationId,
      targetVersion: missing.latestAvailable,
    });
    onForked(result.data.id, result.data.orphanedInputCount);
  };

  const handleDelete = async () => {
    await del.mutateAsync(conversationId);
    onDeleted();
  };

  return (
    <div
      className="bg-warn-dim border-l-4 border-warn/30 rounded-r-md p-6"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-warn shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-warn text-lg font-semibold">
            This conversation references a model version that&apos;s no longer
            available.
          </h3>
          <p className="text-text text-sm leading-relaxed mt-2">
            Conversation{' '}
            <span className="font-mono text-text-bright">
              {conversationTitle}
            </span>{' '}
            was created with{' '}
            <span className="font-mono text-xs bg-panel-bg border border-panel-border px-2 py-1 rounded">
              {missing.modelId}@{missing.modelVersion}
            </span>
            , which has been removed from the registry.
          </p>

          <div className="flex items-center gap-3 mt-4">
            {missing.latestAvailable ? (
              <button
                onClick={handleFork}
                disabled={fork.isPending}
                className={clsx(
                  'py-2 px-4 bg-accent-dim border border-accent/15 rounded-[3px] text-xs font-mono font-semibold text-accent transition-all duration-200',
                  fork.isPending
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-accent/27 hover:bg-accent/13'
                )}
              >
                {fork.isPending
                  ? 'Forking…'
                  : `Fork to ${missing.modelId}@${missing.latestAvailable}`}
              </button>
            ) : (
              <button
                onClick={() => setConfirmingDelete(true)}
                disabled={del.isPending}
                className={clsx(
                  'py-2 px-4 bg-danger-dim border border-danger/15 rounded-[3px] text-xs font-mono font-semibold text-danger transition-all duration-200',
                  del.isPending
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-danger/27'
                )}
              >
                {del.isPending ? 'Deleting…' : 'Delete this conversation'}
              </button>
            )}
            <a
              href={supportMailto({
                subject: `Missing model: ${missing.modelId}`,
              })}
              className="text-xs font-mono text-text-muted hover:text-text underline"
            >
              Contact support ({SUPPORT_EMAIL})
            </a>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmingDelete}
        title="Delete conversation?"
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmingDelete(false)}
      >
        This permanently removes &quot;{conversationTitle}&quot; and all its
        messages.
      </ConfirmModal>
    </div>
  );
}
